import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, tap } from 'rxjs/operators';
import {
  LAFT_FORM_CONSENTS,
  LAFT_ID_COPY_VALIDATION_ERROR,
} from 'src/app/shared/constants/laft-form-consents';
import { LaftService } from 'src/app/shared/services/laft.service';

/**
 * `unchecked` covers both "nothing uploaded" and "uploaded but not yet sent" — neither
 * may reach the submit. Only `valid` opens it.
 */
type IdCopyStatus = 'unchecked' | 'pending' | 'valid' | 'rejected';

@Component({
  selector: 'app-laft-id-copies-form',
  templateUrl: './laft-id-copies-form.component.html',
  styleUrls: ['./laft-id-copies-form.component.scss'],
})
export class LaftIdCopiesFormComponent implements OnInit, OnChanges {
  @Output() nextStep = new EventEmitter();
  @Output() formChangesEvent = new EventEmitter();
  @Input() initData: any;

  /** Declared on step 1. Read-only here: this step collects the images, not the number. */
  @Input() idType: string;
  @Input() idNumber: string;

  formSubmitSubject$ = new Subject();
  public idCopiesForm: FormGroup;
  public consents = LAFT_FORM_CONSENTS;
  public idCopyError = '';

  private idCopyStatus: IdCopyStatus = 'unchecked';

  /**
   * Same labels the investor chose from on step 1 — reused so the confirmation
   * line cannot disagree with the option they picked.
   */
  private readonly documentTypeLabels: Record<string, string> = {
    'national id': 'DNI',
    'foreigner card': 'Carnet de Extranjería',
    passport: 'Pasaporte',
  };

  constructor(private formBuilder: FormBuilder, private laftService: LaftService) {}

  /** A pasaporte carries everything on one side; DNI and carné need both. */
  get isSingleSided(): boolean {
    return this.idType === 'passport';
  }

  get isValidatingIdCopy(): boolean {
    return this.idCopyStatus === 'pending';
  }

  /** The submit stays shut until the check comes back clean — not merely once a file is attached. */
  get isIdCopyVerified(): boolean {
    return this.idCopyStatus === 'valid';
  }

  /**
   * Both the verdict on the front side and the back side itself. The check only ever
   * reads the front, so a two-sided document can be verified with nothing on the back.
   */
  get canSubmit(): boolean {
    return this.isIdCopyVerified && (this.isSingleSided || !!this.idCopiesForm?.get('idBackPicture')?.value);
  }

  get declaredDocument(): string {
    const typeLabel = this.documentTypeLabels[this.idType] || '';

    return `${typeLabel} ${this.idNumber || ''}`.trim();
  }

  ngOnInit(): void {
    this.idCopiesForm = this.formBuilder.group({
      idFrontPicture: ['', [Validators.required, () => this.idCopyAccepted()]],
      idBackPicture: ['', [Validators.required]],
      acceptsContract: [false, [Validators.requiredTrue]],
      acceptsCommercialUse: [false, []],
    });

    this.applyDocumentSideRules();

    // Clearing the file resets the check — the next upload is a different document and
    // has to earn its own verdict.
    this.idCopiesForm
      .get('idFrontPicture')
      ?.valueChanges.pipe(filter(value => !value && this.idCopyStatus !== 'unchecked'))
      .subscribe(() => {
        this.idCopyStatus = 'unchecked';
        this.idCopyError = '';
      });

    this.idCopiesForm.valueChanges.pipe(debounceTime(300), distinctUntilChanged()).subscribe(() => {
      this.formChangesEvent.emit(this.removeUnusedSide(this.idCopiesForm.value));
    });

    this.formSubmitSubject$
      .pipe(
        tap(() => {
          this.idCopiesForm.markAsDirty();
          this.idCopiesForm.markAllAsTouched();
          Object.keys(this.idCopiesForm.controls).forEach(key => {
            this.idCopiesForm.get(key)?.updateValueAndValidity({ emitEvent: true });
          });
        }),
        filter(() => this.idCopiesForm.valid)
      )
      .subscribe(() => {
        this.nextStep.next(this.removeUnusedSide(this.idCopiesForm.value));
      });

    this.populateFormData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.idType && this.idCopiesForm) {
      this.applyDocumentSideRules();
    }
  }

  onContinue(): void {
    this.formSubmitSubject$.next();
  }

  /**
   * Fires on a fresh upload only, never on `valueChanges`. A re-opened draft pre-fills the
   * control with the stored reference rather than the image, and sending that would ask
   * the OCR to read a storage key.
   */
  onFrontPictureLoaded(image: string): void {
    if (!image) {
      return;
    }

    this.idCopyStatus = 'pending';
    this.idCopyError = '';
    this.refreshFrontPictureValidity();

    this.laftService.validateIdCopy(image).subscribe({
      next: response => this.settleIdCopyCheck(response?.isValid === true),
      // A timeout, a 5xx or a provider outage is a failure like any other: the same
      // message, still retryable. It must never advance an unverified document, and it
      // must never leave the step stuck.
      error: () => this.settleIdCopyCheck(false),
    });
  }

  private settleIdCopyCheck(isValid: boolean): void {
    this.idCopyStatus = isValid ? 'valid' : 'rejected';
    this.idCopyError = isValid ? '' : LAFT_ID_COPY_VALIDATION_ERROR;
    this.refreshFrontPictureValidity();
  }

  /** The submit is gated on the verdict, not merely on a file being present. */
  private idCopyAccepted(): ValidationErrors | null {
    return this.idCopyStatus === 'valid' ? null : { idCopyNotValidated: true };
  }

  private refreshFrontPictureValidity(): void {
    this.idCopiesForm.get('idFrontPicture')?.updateValueAndValidity({ emitEvent: false });
  }

  /**
   * The back side is required for every document except a pasaporte. Mirrors the
   * rule the registration control already applies in idTypeChanged().
   */
  private applyDocumentSideRules(): void {
    const backPicture = this.idCopiesForm.get('idBackPicture');

    if (this.isSingleSided) {
      backPicture?.clearValidators();
      backPicture?.setValue('');
    } else {
      backPicture?.setValidators([Validators.required]);
    }

    backPicture?.updateValueAndValidity({ emitEvent: false });
  }

  private populateFormData(): void {
    // The front side always starts empty. A copy saved on an earlier visit comes back as a
    // stored reference rather than the image, and one saved before this check existed was
    // never validated at all — neither can be verified from here, so the investor uploads
    // again. The back side is stored but never read, so it survives untouched.
    this.idCopiesForm.patchValue({
      idFrontPicture: '',
      idBackPicture: this.initData?.idBackPicture || '',
      acceptsContract: this.initData?.acceptsContract ?? false,
      acceptsCommercialUse: this.initData?.acceptsCommercialUse ?? false,
    });
  }

  private removeUnusedSide(data: any): any {
    const cleaned = { ...data };

    if (this.isSingleSided) {
      cleaned.idBackPicture = '';
    }

    return cleaned;
  }
}
